import { useTranslation } from '@/hooks/use-translation';
import { motion } from 'motion/react';

export function Footer() {
  const { t } = useTranslation();

  return (
    <footer
      id="contact"
      className="bg-slate-100 dark:bg-[#0a0f14] border-t border-slate-200 dark:border-[#1a2530]"
    >
      {/* Contact section */}
      <div className="max-w-6xl mx-auto px-6 py-16 lg:py-24">
        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-block mb-4 text-[#3d6a7a] dark:text-[#7ab8c9] text-sm md:text-base tracking-[0.2em] uppercase font-medium">
            {t('footer.label')}
          </span>
          <h2 className="font-display text-slate-900 dark:text-[#e8ebe9] text-4xl md:text-5xl lg:text-6xl leading-tight mb-6 tracking-tight">
            {t('footer.title')}
          </h2>
          <p className="text-slate-600 dark:text-[#8a9a9e] text-base md:text-lg max-w-xl mx-auto mb-10 leading-relaxed">
            {t('footer.description')}
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="#contact-form"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-lg bg-[#3d6a7a] hover:bg-[#2d5a6a] dark:bg-[#5a8a9a] text-white dark:text-[#0a0f14] text-sm md:text-base font-medium tracking-wide dark:hover:bg-[#7ab8c9] transition-all duration-300 shadow-md hover:shadow-lg"
            >
              {t('footer.requestConversation')}
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M14 5l7 7m0 0l-7 7m7-7H3"
                />
              </svg>
            </a>
            <a
              href="#"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-lg border border-slate-300 dark:border-[#3d5a6e] text-slate-800 dark:text-[#e8ebe9] text-sm md:text-base font-medium tracking-wide hover:bg-slate-50 dark:hover:bg-[#1a2530] transition-all duration-300 hover:border-slate-400 dark:hover:border-[#5a8a9a]"
            >
              {t('footer.downloadOverview')}
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </a>
          </div>
        </motion.div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-slate-200 dark:border-[#1a2530]">
        <div className="max-w-6xl mx-auto px-6 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 relative">
                <svg viewBox="0 0 32 32" className="w-full h-full">
                  <rect
                    x="2"
                    y="2"
                    width="12"
                    height="12"
                    fill="none"
                    stroke="#5a8a9a"
                    strokeWidth="1.5"
                  />
                  <rect
                    x="18"
                    y="2"
                    width="12"
                    height="12"
                    fill="#5a8a9a"
                    opacity="0.3"
                  />
                  <rect
                    x="2"
                    y="18"
                    width="12"
                    height="12"
                    fill="#5a8a9a"
                    opacity="0.5"
                  />
                  <rect
                    x="18"
                    y="18"
                    width="12"
                    height="12"
                    fill="none"
                    stroke="#5a8a9a"
                    strokeWidth="1.5"
                  />
                </svg>
              </div>
              <span className="font-display text-slate-600 dark:text-[#8a9a9e] text-lg md:text-xl">
                Urban Maps
              </span>
            </div>

            {/* Links */}
            <div className="flex items-center gap-6 md:gap-8">
              <a
                href="#problem"
                className="text-slate-500 dark:text-[#6a7a7e] text-sm md:text-base hover:text-[#3d6a7a] dark:hover:text-[#7ab8c9] transition-colors"
              >
                {t('nav.context')}
              </a>
              <a
                href="#solution"
                className="text-slate-500 dark:text-[#6a7a7e] text-sm md:text-base hover:text-[#3d6a7a] dark:hover:text-[#7ab8c9] transition-colors"
              >
                {t('nav.solution')}
              </a>
              <a
                href="#users"
                className="text-slate-500 dark:text-[#6a7a7e] text-sm md:text-base hover:text-[#3d6a7a] dark:hover:text-[#7ab8c9] transition-colors"
              >
                {t('nav.users')}
              </a>
              <a
                href="#revenue"
                className="text-slate-500 dark:text-[#6a7a7e] text-sm md:text-base hover:text-[#3d6a7a] dark:hover:text-[#7ab8c9] transition-colors"
              >
                {t('nav.revenue')}
              </a>
            </div>

            {/* Copyright */}
            <span className="text-slate-400 dark:text-[#4a5a5e] text-xs md:text-sm">
              {t('footer.copyright')}
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
